/**
 * Implements the WP5 client protocol (`docs/specs/WP5-realtime.md`): subscribe to every
 * `/topic/room/{roomId}/…` destination first, buffer what arrives, fetch the REST snapshot,
 * hydrate the store, then replay the buffer through the store's version gates — and repeat that
 * whole sequence on every reconnect, not just the first connect.
 *
 * `fields`/`locks`/`section`/`reveal`/`room`/`members` are buffered: they overlap what `hydrate()`
 * itself sets, so applying one before the snapshot lands would just get wiped out by the hydrate
 * that follows a moment later. `presence` and `invites` are not part of `RoomSnapshot` at all
 * (presence is a live-only stream; invites live in TanStack Query's cache), so they're applied
 * immediately — there is nothing for a hydrate to overwrite.
 *
 * WP6 split the WP5 `room` topic in two: `room` now carries the room row only, and `members`
 * carries the member list (wrapped with the `roomVersion` the membership mutation bumped to) on
 * its own topic — the Overlay principal (WP6's OBS page) may subscribe `room` but is denied
 * `members`, since the overlay never shows people.
 *
 * Locks and section changes don't have their own per-item version the way a field cell does, so
 * they're gated the same way `applyRoom`/`applyReveal` already gate themselves: against the
 * snapshot's `room.roomVersion`, a single counter the backend bumps on every room-scoped mutation.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { apiGet } from './api';
import { meQuery, type Me } from './auth';
import { pushToast } from './events';
import type { FieldValue, RowKind } from './fieldValue';
import type { OverlayRoom } from './overlay';
import { activate, deactivate, onConnect, publish, subscribe } from './stomp';
import {
  invitesQuery,
  roomQuery,
  type LockTargetKind,
  type Reveal,
  type RoomSnapshot,
} from './rooms';
import { useRoomStore, type FieldBroadcast } from '../store/roomStore';
import { usePresenceStore, type Presence, type PresenceFocus } from '../store/presenceStore';

/** The WP5/WP6 `fields` topic, people-free by contract: no `updatedBy` (see
 * `docs/specs/WP5-realtime.md`'s contract table). */
interface FieldMessage {
  fieldId: number;
  rowKind: RowKind;
  value: FieldValue | null;
  version: number;
  updatedAt: string | null;
}

/** The WP5/WP6 `locks` topic, people-free by contract: no `lockedBy`. */
interface LocksMessage {
  targetKind: LockTargetKind;
  targetId: number;
  locked: boolean;
  roomVersion: number;
}

interface SectionMessage {
  sectionId: number | null;
  enteredAt: string | null;
  roomVersion: number;
}

interface RevealMessage {
  reveal: Reveal | null;
  roomVersion: number;
}

/** The WP6-split `room` topic: the people-free `OverlayRoom` shape wrapped with the `roomVersion`
 * the mutation bumped to — `roomStore.applyRoom` merges this into the existing snapshot room,
 * preserving `createdBy`/`guestUserId` (absent from this broadcast). */
interface RoomMessage {
  room: OverlayRoom;
  roomVersion: number;
}

/** The WP6-split `members` topic: User principals only. Carries `guestUserId` alongside the member
 * list because a membership change can itself change who the guest is. */
interface MembersMessage {
  members: RoomSnapshot['members'];
  guestUserId: number | null;
  roomVersion: number;
}

/** Subscribes this room's realtime topics for the lifetime of the calling component, and returns
 * `sendFocus` for `RatingTable`/`FieldEditor` to announce/clear presence focus. Safe to call with a
 * non-finite `roomId` (e.g. an unparsable route param) — it simply does nothing. */
export function useRoomRealtime(roomId: number): { sendFocus: (focus: PresenceFocus | null) => void } {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sendFocusRef = useRef<(focus: PresenceFocus | null) => void>(() => {});

  useEffect(() => {
    if (!Number.isFinite(roomId)) {
      return;
    }

    const store = useRoomStore;
    let hydrated = false;
    let buffer: (() => void)[] = [];
    let invitesUnsubscribe: (() => void) | null = null;
    // The last focus this client asked to announce. `publish()` silently drops a message sent
    // while the socket isn't connected yet (see `lib/stomp.ts`) — a real race if a cell is opened
    // right after page load, before the first CONNECT completes — and a genuine reconnect starts
    // the server's presence entry over from nothing either way. Re-sending this on every (re)connect
    // (below) closes both gaps; it's the client-side mirror of buffering incoming broadcasts.
    let lastFocus: PresenceFocus | null = null;
    // Per-hook-instance counter the backend uses to order presence messages that could otherwise
    // race (two focus changes in flight at once): every actual message this client puts on the wire
    // — the debounced send below, the on-connect resend, and the cleanup flush — takes the next
    // value, so a later message always carries a strictly higher `seq` than an earlier one.
    let seq = 0;
    // Debounces outgoing focus sends ~60ms trailing so a StrictMode-style burst (`A`, `null`, `A`
    // from a double effect run) collapses into the single message the settled state actually needs,
    // instead of three separate ones racing each other over the wire.
    let focusDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    function publishFocus(focus: PresenceFocus | null): void {
      seq += 1;
      publish(`/app/room/${roomId}/presence`, { focus, seq });
    }

    function sendFocus(focus: PresenceFocus | null): void {
      lastFocus = focus;
      if (focusDebounceTimer !== null) {
        clearTimeout(focusDebounceTimer);
      }
      focusDebounceTimer = setTimeout(() => {
        focusDebounceTimer = null;
        publishFocus(focus);
      }, 60);
    }
    sendFocusRef.current = sendFocus;

    function currentRoomVersion(): number {
      return store.getState().snapshot?.room.roomVersion ?? -1;
    }

    function runOrBuffer(fn: () => void): void {
      if (hydrated) {
        fn();
      } else {
        buffer.push(fn);
      }
    }

    function checkMembership(members: RoomSnapshot['members']): void {
      const myId = queryClient.getQueryData<Me>(meQuery.queryKey)?.id;
      if (myId === undefined) {
        return;
      }
      if (!members.some((member) => member.userId === myId)) {
        pushToast('You were removed from this room.');
        navigate('/');
      }
    }

    function syncInvitesSubscription(): void {
      const myRole = store.getState().snapshot?.myRole;
      const eligible = myRole === 'HOST' || myRole === 'COHOST';
      if (eligible && !invitesUnsubscribe) {
        // The payload carries only `roomVersion` — a plain invalidate-and-refetch is the whole
        // reaction (`HostRailInvites` re-fetches the invite list).
        invitesUnsubscribe = subscribe(`/topic/room/${roomId}/invites`, () => {
          void queryClient.invalidateQueries({ queryKey: invitesQuery(roomId).queryKey });
        });
      } else if (!eligible && invitesUnsubscribe) {
        invitesUnsubscribe();
        invitesUnsubscribe = null;
      }
    }

    async function fetchHydrateAndReplay(): Promise<void> {
      // Only the "hydrated" flag resets here, not the buffer itself: a message that arrived
      // between the (re)connect and this fetch resolving must still be replayed afterwards, not
      // discarded — the version gates below are what protect against anything in it being stale.
      hydrated = false;
      try {
        const snapshot = await apiGet<RoomSnapshot>(`/api/rooms/${roomId}`);
        queryClient.setQueryData(roomQuery(roomId).queryKey, snapshot);
        store.getState().hydrate(snapshot);
        checkMembership(snapshot.members);
        syncInvitesSubscription();
      } catch {
        // A transient fetch failure here just means the store stays on its last-known snapshot;
        // the next successful (re)connect retries the whole sequence.
        return;
      }
      hydrated = true;
      const toReplay = buffer;
      buffer = [];
      for (const apply of toReplay) {
        apply();
      }
    }

    const unsubscribers = [
      subscribe(`/topic/room/${roomId}/fields`, (body) => {
        const cell = body as FieldMessage;
        runOrBuffer(() => store.getState().applyFieldChange(cell satisfies FieldBroadcast));
      }),
      subscribe(`/topic/room/${roomId}/locks`, (body) => {
        const message = body as LocksMessage;
        runOrBuffer(() => {
          if (message.roomVersion <= currentRoomVersion()) {
            return;
          }
          if (message.locked) {
            store.getState().applyLock({
              targetKind: message.targetKind,
              targetId: message.targetId,
              // People-free by contract (WP5/WP6) — no `lockedBy` on this topic.
              lockedAt: new Date().toISOString(),
            });
          } else {
            store.getState().applyUnlock(message.targetKind, message.targetId);
          }
        });
      }),
      subscribe(`/topic/room/${roomId}/section`, (body) => {
        const message = body as SectionMessage;
        runOrBuffer(() => store.getState().applySection(message));
      }),
      subscribe(`/topic/room/${roomId}/reveal`, (body) => {
        const message = body as RevealMessage;
        runOrBuffer(() => store.getState().applyReveal(message));
      }),
      subscribe(`/topic/room/${roomId}/room`, (body) => {
        const message = body as RoomMessage;
        runOrBuffer(() => store.getState().applyRoom(message));
      }),
      subscribe(`/topic/room/${roomId}/members`, (body) => {
        const message = body as MembersMessage;
        runOrBuffer(() => {
          // Gate here (not just inside `applyMembers`) so a stale/replayed frame — one the store
          // silently drops — never runs `checkMembership` against member data that doesn't
          // reflect the local room's actual current state.
          if (message.roomVersion <= currentRoomVersion()) {
            return;
          }
          store.getState().applyMembers(message);
          checkMembership(message.members);
          syncInvitesSubscription();
        });
      }),
      subscribe(`/topic/room/${roomId}/presence`, (body) => {
        const list = body as Presence[];
        usePresenceStore.getState().setPresence(roomId, list);
      }),
    ];

    const unsubscribeConnect = onConnect(() => {
      void fetchHydrateAndReplay();
      // Re-announce whatever focus was last requested — see the comment on `lastFocus` above. Sent
      // immediately (not through the debounce): a (re)connect is a one-off resync event, not part of
      // a UI burst, and `publishFocus` still hands it a fresh, higher `seq` than anything before it.
      if (lastFocus !== null) {
        publishFocus(lastFocus);
      }
    });

    activate();

    return () => {
      sendFocusRef.current = () => {};
      if (focusDebounceTimer !== null) {
        clearTimeout(focusDebounceTimer);
        focusDebounceTimer = null;
      }
      // Flush a final clear synchronously, bypassing the debounce entirely, so a room left mid-edit
      // (e.g. the field editor's own unmount just queued a debounced `null`, then this hook itself
      // tears down before that timer fires) never leaves this client's last-known focus stale on the
      // server after the socket goes away.
      publishFocus(null);
      unsubscribeConnect();
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
      invitesUnsubscribe?.();
      usePresenceStore.getState().clearRoom(roomId);
      deactivate();
    };
  }, [roomId, queryClient, navigate]);

  return { sendFocus: useCallback((focus: PresenceFocus | null) => sendFocusRef.current(focus), []) };
}
